import cn from 'classnames';
import { ChangeEvent, MouseEvent } from 'react';
import { Row, Col, Form, DropdownButton, Dropdown } from 'react-bootstrap';
import { HiOutlinePlus } from 'react-icons/hi';
import { MdDelete } from 'react-icons/md';
import { Plover } from '@blisspointmedia/bpm-types/dist/webTrafficOptimization';
import { Button } from '@Components/Button/Button';
import { IconButton } from '@Components/IconButton';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { DAY_OF_WEEK_OPTIONS } from '@src/utils/date-utils';
import { Size } from '@Components/designConstants';
import { WtoDowOperation, WtoRule } from '../../wto-form-context';
import { TimeSelection } from './TimeSelection';
import { VariantSplits } from './VariantSplits';
import { TIMEZONE_MAP_KEYS } from './rules-form-modal-constants';

interface DayOfWeekSectionProps {
  newRule: WtoRule;
  wtoFormObj: Plover;
  readOnly: boolean;
  selectDay: (day: { number: number; label: string }, index: number) => void;
  handleDowTime: (
    value: string | null,
    type: 'hour' | 'minute',
    bound: 'start' | 'end',
    index: number
  ) => void;
  handleDowPeriod: (period: 'am' | 'pm' | null, bound: 'start' | 'end', index: number) => void;
  handleDowTimezone: (timezone: string | null, index: number) => void;
  handleDowVariant: (
    operation: 'select' | 'allocate' | 'delete',
    index: number,
    rowIndex?: number,
    data?: unknown
  ) => void;
  addDayOfWeekSplitRow: () => void;
  addDowTrafficSplit: (index: number) => void;
  onSelectChange: (
    event: unknown,
    keyName?: 'additional_utm' | 'additional_utm_operation' | 'variant_id' | 'cookie_operation',
    index?: number,
    index2?: number
  ) => void;
  onInputChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    valueType?: string,
    idx?: number
  ) => void;
  onDeleteOperationClick: (
    e: MouseEvent<HTMLElement>,
    ruleType:
      | 'conditions'
      | 'cookie_operation'
      | 'additional_utm'
      | 'additional_utm_operation'
      | 'variant_splits',
    optrnIdx?: number
  ) => void;
  addTrafficSplit: () => void;
}

export const DayOfWeekSection = ({
  newRule,
  wtoFormObj,
  readOnly,
  selectDay,
  handleDowTime,
  handleDowPeriod,
  handleDowTimezone,
  handleDowVariant,
  addDayOfWeekSplitRow,
  addDowTrafficSplit,
  onSelectChange,
  onInputChange,
  onDeleteOperationClick,
  addTrafficSplit
}: DayOfWeekSectionProps) => (
  <Row>
    <Form.Group as={Col} className='rule-editor'>
      <span className='rule-header'>Day of the Week Split</span>
      <div className='dow-operations'>
        {newRule.dow_operation.map((operation: WtoDowOperation, index: number) => (
          <div key={index}>
            <hr />
            <div className='dow-header'>Select the day of week</div>
            <div className='dow-controls'>
              <div className='dow-selections'>
                {DAY_OF_WEEK_OPTIONS.map(day => {
                  const isSelected = operation.day.includes(day.number);
                  const isUsedElsewhere =
                    !isSelected &&
                    newRule.dow_operation
                      .flatMap((dowOperation: WtoDowOperation) => dowOperation.day)
                      .includes(day.number);
                  return (
                    <InteractiveDiv
                      key={day.number}
                      className={cn('dow-selection', {
                        selected: isSelected,
                        disabled: isUsedElsewhere
                      })}
                      onClick={() => selectDay(day, index)}
                    >
                      {day.label}
                    </InteractiveDiv>
                  );
                })}
              </div>
              <div className='time-range-wrapper'>
                Start Time:{' '}
                <TimeSelection
                  readOnly={readOnly}
                  handleHour={hour => handleDowTime(hour, 'hour', 'start', index)}
                  handleMinute={minute => handleDowTime(minute, 'minute', 'start', index)}
                  handleAmpm={period => handleDowPeriod(period, 'start', index)}
                  timestamp={newRule.dow_operation[index].start_time}
                />
                End Time:{' '}
                <TimeSelection
                  readOnly={readOnly}
                  handleHour={hour => handleDowTime(hour, 'hour', 'end', index)}
                  handleMinute={minute => handleDowTime(minute, 'minute', 'end', index)}
                  handleAmpm={period => handleDowPeriod(period, 'end', index)}
                  timestamp={newRule.dow_operation[index].end_time}
                />
                <DropdownButton
                  disabled={readOnly}
                  className='condition-dropdown'
                  title={
                    newRule.dow_operation[index].time_zone
                      ? newRule.dow_operation[index].time_zone
                      : 'Select Timezone'
                  }
                >
                  {TIMEZONE_MAP_KEYS.map(timezone => (
                    <Dropdown.Item
                      key={timezone}
                      eventKey={timezone}
                      onClick={() => handleDowTimezone(timezone, index)}
                    >
                      {timezone}
                    </Dropdown.Item>
                  ))}
                </DropdownButton>
                {!readOnly && (
                  <IconButton
                    className='delete-button'
                    data-index={index}
                    onClick={e => {
                      e.preventDefault();
                      handleDowVariant('delete', index);
                    }}
                  >
                    <MdDelete />
                  </IconButton>
                )}
              </div>
            </div>
            {operation.day.length > 0 && (
              <VariantSplits
                variants={operation.variants}
                dowIndex={index}
                readOnly={readOnly}
                newRule={newRule}
                wtoFormObj={wtoFormObj}
                onSelectChange={onSelectChange}
                onInputChange={onInputChange}
                onDeleteOperationClick={onDeleteOperationClick}
                handleDowVariant={handleDowVariant}
                addTrafficSplit={addTrafficSplit}
                addDowTrafficSplit={addDowTrafficSplit}
              />
            )}
          </div>
        ))}
      </div>
      {newRule.dow_operation.length > 0 && <hr />}
      <Button
        size={Size.LG}
        className='add-rule'
        leadingElement={<HiOutlinePlus />}
        disabled={readOnly}
        onClick={e => {
          e.preventDefault();
          addDayOfWeekSplitRow();
        }}
      >
        Add Row
      </Button>
    </Form.Group>
  </Row>
);
